import { motion } from 'framer-motion';
import { ChipGroup, ChipOption } from './ChipGroup';
import { TimeInput } from './TimeInput';
import { ToggleSwitch } from './ToggleSwitch';
import { sectionVariants } from './settings.animations';
import styles from '../Settings.module.css';

export interface DeepWorkSectionProps {
  fixedScheduleEnabled: boolean;
  fixedScheduleEndTime: string;
  fixedScheduleReminderMinutes: number;
  showDeepWorkMetrics: boolean;
  weeklyPlanningEnabled: boolean;
  dayThemingEnabled: boolean;
  reminderOptions: ChipOption<number>[];
  onFixedScheduleEnabledChange: (value: boolean) => void;
  onFixedScheduleEndTimeChange: (value: string) => void;
  onFixedScheduleReminderMinutesChange: (value: number) => void;
  onShowDeepWorkMetricsChange: (value: boolean) => void;
  onWeeklyPlanningEnabledChange: (value: boolean) => void;
  onDayThemingEnabledChange: (value: boolean) => void;
}

export function DeepWorkSection({
  fixedScheduleEnabled,
  fixedScheduleEndTime,
  fixedScheduleReminderMinutes,
  showDeepWorkMetrics,
  weeklyPlanningEnabled,
  dayThemingEnabled,
  reminderOptions,
  onFixedScheduleEnabledChange,
  onFixedScheduleEndTimeChange,
  onFixedScheduleReminderMinutesChange,
  onShowDeepWorkMetricsChange,
  onWeeklyPlanningEnabledChange,
  onDayThemingEnabledChange,
}: DeepWorkSectionProps) {
  return (
    <motion.section
      className={styles.section}
      variants={sectionVariants}
      aria-labelledby="calnewport-heading"
    >
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon} aria-hidden="true">
          T
        </span>
        <h2 id="calnewport-heading" className={styles.sectionTitle}>
          Deep Work Features
        </h2>
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Fixed-Schedule Productivity</p>
          <p className={styles.settingDesc}>
            Set a firm end time for your workday. Get reminded as you approach it.
          </p>
        </div>
        <ToggleSwitch
          checked={fixedScheduleEnabled}
          onChange={onFixedScheduleEnabledChange}
          label="Enable fixed schedule"
        />
      </div>

      {fixedScheduleEnabled && (
        <>
          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <p className={styles.settingLabel}>Work End Time</p>
              <p className={styles.settingDesc}>When your workday should end</p>
            </div>
            <TimeInput
              value={fixedScheduleEndTime}
              onChange={onFixedScheduleEndTimeChange}
              label="Fixed schedule end time"
            />
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <p className={styles.settingLabel}>Reminder Before</p>
              <p className={styles.settingDesc}>Minutes before end time to show warning</p>
            </div>
            <ChipGroup
              options={reminderOptions}
              value={fixedScheduleReminderMinutes}
              onChange={onFixedScheduleReminderMinutesChange}
              label="Reminder time"
            />
          </div>
        </>
      )}

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Show Deep Work Metrics</p>
          <p className={styles.settingDesc}>
            Display daily and weekly deep work tracking on the dashboard
          </p>
        </div>
        <ToggleSwitch
          checked={showDeepWorkMetrics}
          onChange={onShowDeepWorkMetricsChange}
          label="Show deep work metrics"
        />
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Weekly Planning</p>
          <p className={styles.settingDesc}>Enable weekly goals and the weekly planning view</p>
        </div>
        <ToggleSwitch
          checked={weeklyPlanningEnabled}
          onChange={onWeeklyPlanningEnabledChange}
          label="Enable weekly planning"
        />
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Day Theming</p>
          <p className={styles.settingDesc}>
            Assign themes to days for batched focus (e.g., Deep Work Mondays)
          </p>
        </div>
        <ToggleSwitch
          checked={dayThemingEnabled}
          onChange={onDayThemingEnabledChange}
          label="Enable day theming"
        />
      </div>
    </motion.section>
  );
}
